import type { Graphics } from 'pixi.js';
import { smooth } from '../../burst';
import { lerpColor } from '../../color';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const RIBS = 8;
const SPIN = 0.15;
const RUNES = 8;
const MINIS = 6;
const SEG = TAU / RIBS;

function wedge(g: Graphics, body: Body, r: number, a0: number, a1: number): void {
  g.moveTo(body.x, body.y);
  for (let s = 0; s <= 4; s++) {
    const a = a0 + ((a1 - a0) * s) / 4;
    g.lineTo(body.x + Math.cos(a) * r * body.scaleX, body.y + Math.sin(a) * r * body.scaleY);
  }
  g.closePath();
}

function ribs(g: Graphics, body: Body, r: number, turn: number): void {
  for (let i = 0; i < RIBS; i++) {
    const a = turn + i * SEG;
    g.moveTo(body.x, body.y).lineTo(body.x + Math.cos(a) * r * body.scaleX, body.y + Math.sin(a) * r * body.scaleY);
  }
}

function mini(g: Graphics, x: number, y: number, s: number, turn: number, a: number, b: number, edge: number, ink: number, alpha: number, width: number): void {
  const body: Body = { shape: 'circle', x, y, radius: s, rotation: 0, scaleX: 1, scaleY: 1 };
  for (let i = 0; i < 4; i += 2) wedge(g, body, s, turn + (i * TAU) / 4, turn + ((i + 1) * TAU) / 4);
  g.fill({ color: a, alpha });
  for (let i = 1; i < 4; i += 2) wedge(g, body, s, turn + (i * TAU) / 4, turn + ((i + 1) * TAU) / 4);
  g.fill({ color: b, alpha });
  g.circle(x, y, Math.max(1, s * 0.16)).fill({ color: ink, alpha });
  g.circle(x, y, s).stroke({ width, color: edge, alpha });
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const turn = -Math.PI / 2;
    for (let i = 1; i < RIBS; i += 2) wedge(g, body, r * 0.97, turn + i * SEG, turn + (i + 1) * SEG);
    g.fill({ color: lerpColor(color, pal.ink, 0.16), alpha: 0.35 * alpha });
    ribs(g, body, r * 0.95, turn);
    g.stroke({ width: Math.max(1, r * 0.03), color: lerpColor(color, pal.ink, 0.4), alpha: 0.15 * alpha, cap: 'round' });
    g.circle(body.x, body.y, Math.max(1.2, r * 0.07)).fill({ color: lerpColor(color, pal.ink, 0.5), alpha: 0.6 * alpha });
    k.highlight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill, seed) {
    const { g, pal } = k;
    const r = body.radius;
    const turn = (k.reduced ? 0 : k.time * TAU * SPIN) + seed * 0.7;
    const tones = [fill, lerpColor(fill, pal.spot, 0.15), lerpColor(fill, pal.heldLight, 0.2)];
    const rib = lerpColor(fill, pal.ink, 0.03);
    for (let c = 0; c < 3; c++) {
      for (let i = 0; i < RIBS; i++) {
        const tone = i % 2 === 0 ? 0 : i % 4 === 1 ? 1 : 2;
        if (tone === c) wedge(g, body, r, turn + i * SEG, turn + (i + 1) * SEG);
      }
      g.fill({ color: tones[c] ?? fill, alpha });
    }
    k.terminator(body, pal.ink, 0.16 * alpha);
    for (let i = 0; i < RIBS; i++) {
      const a0 = turn + i * SEG;
      const am = a0 + SEG / 2;
      const a1 = a0 + SEG;
      g.moveTo(body.x + Math.cos(a0) * r * 0.93 * body.scaleX, body.y + Math.sin(a0) * r * 0.93 * body.scaleY).quadraticCurveTo(
        body.x + Math.cos(am) * r * 0.8 * body.scaleX,
        body.y + Math.sin(am) * r * 0.8 * body.scaleY,
        body.x + Math.cos(a1) * r * 0.93 * body.scaleX,
        body.y + Math.sin(a1) * r * 0.93 * body.scaleY,
      );
    }
    g.stroke({ width: Math.max(1, r * 0.022), color: rib, alpha });
    ribs(g, body, r * 0.93, turn);
    g.stroke({ width: Math.max(1, r * 0.03), color: rib, alpha, cap: 'round' });
    g.circle(body.x, body.y, Math.max(1.5, r * 0.085)).fill({ color: pal.ink, alpha });
    g.circle(body.x, body.y, Math.max(0.8, r * 0.04)).fill({ color: pal.heldLight, alpha });
    k.highlight(body, 0.4 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    const s = Math.max(3.5, k.unit * 0.02);
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    g.ellipse(body.x, body.y, reach * body.scaleX, reach * body.scaleY);
    g.stroke({ width: Math.max(1, edge * 0.55), color: pal.spot, alpha: 0.28 * alpha });
    const from = body.radius * 1.04 + 1;
    const to = reach - s * 0.2;
    if (to > from) {
      k.ringDots(body, reach, RUNES, spin, (_x, _y, _i, a) => {
        g.moveTo(body.x + Math.cos(a) * from, body.y + Math.sin(a) * from).lineTo(body.x + Math.cos(a) * to, body.y + Math.sin(a) * to);
      });
      g.stroke({ width: Math.max(1, edge * 0.45), color: pal.spot, alpha: 0.42 * alpha, cap: 'round' });
    }
    k.ringDots(body, reach, RUNES, spin, (x, y, _i, a) => {
      g.moveTo(x + Math.cos(a - Math.PI / 2) * s, y + Math.sin(a - Math.PI / 2) * s);
      g.arc(x, y, s, a - Math.PI / 2, a + Math.PI / 2);
      g.closePath();
    });
    g.fill({ color: pal.spot, alpha: 0.6 * alpha });
    k.ringDots(body, reach, RUNES, spin, (x, y, _i, a) => {
      for (const d of [-Math.PI / 4, Math.PI / 4]) g.moveTo(x, y).lineTo(x + Math.cos(a + d) * s, y + Math.sin(a + d) * s);
    });
    g.stroke({ width: Math.max(1, s * 0.16), color: pal.ink, alpha: 0.5 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.incoming.circle : pal.held;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.18 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, pal.rim, 0.2) : lerpColor(pal.bg, pal.rim, 0.16), alpha });
    g.circle(x, y, radius * 0.55).fill({ color: on ? lerpColor(tone, pal.ink, 0.45) : lerpColor(pal.bg, pal.ink, 0.3), alpha: 0.9 * alpha });
    g.circle(x, y, radius).stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    if (on) g.circle(x - radius * 0.42, y - radius * 0.42, radius * 0.18).fill({ color: pal.rim, alpha: 0.7 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(7, k.unit * 0.04);
    let n = 0;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const nx = -dy / len;
      const ny = dx / len;
      const steps = Math.min(60, Math.floor(len / step));
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        const side = (n++ % 2 === 0 ? 1 : -1) * step * 0.32;
        g.circle(x0 + dx * t + nx * side, y0 + dy * t + ny * side, 1.5);
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      g.moveTo(x, y);
      g.arc(x, y, size * 1.5, rot, rot + 0.9);
      g.closePath();
    } else g.regularPoly(x, y, size, 4, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const spin = m.grow * Math.PI;
    g.circle(m.x, m.y, m.r).stroke({ width: m.width, color: pal.held, alpha: m.fade * 0.6 });
    for (let i = 0; i < RIBS; i++) {
      const a = spin + i * SEG;
      g.moveTo(m.x + Math.cos(a) * m.r * 0.9, m.y + Math.sin(a) * m.r * 0.9).lineTo(m.x + Math.cos(a) * m.r * 1.1, m.y + Math.sin(a) * m.r * 1.1);
    }
    g.stroke({ width: m.width * 0.8, color: pal.spot, alpha: m.fade * 0.7, cap: 'round' });
    const s = m.unit * 0.04 * smooth(Math.min(1, m.grow * 2.5));
    if (s < 1) return;
    for (let j = 0; j < MINIS; j++) {
      const a = -spin * 0.5 + (j * TAU) / MINIS + Math.PI / 6;
      const x = m.x + Math.cos(a) * m.r * 1.3;
      const y = m.y + Math.sin(a) * m.r * 1.3;
      mini(g, x, y, s, spin + j, j % 2 === 0 ? pal.held : pal.spot, j % 2 === 0 ? pal.spot : pal.heldLight, pal.rim, pal.ink, m.fade * 0.85, Math.max(1, m.width * 0.5));
    }
  },

  trail(g, x, y, size, angle) {
    const r = size * 1.4;
    const end = angle + Math.PI * 1.1;
    const mid = angle + Math.PI * 0.55;
    g.moveTo(x + Math.cos(angle) * r, y + Math.sin(angle) * r);
    g.arc(x, y, r, angle, end);
    g.arc(x + Math.cos(mid) * r * 0.35, y + Math.sin(mid) * r * 0.35, r * 0.72, end, angle, true);
    g.closePath();
  },
};
